import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api, localTimestamp } from '../api';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getWordsByGroup, putProgress, getLearnableGroups } from '../db';
import { triggerAutoSync } from '../sync';
import './TestMode.css';

function TestMode() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // 活动类型：test / review1 / review2 / review3（首页点击复习时通过 ?type= 传入；缺省为 test）
    const activityType = searchParams.get('type') || 'test';
    const isOnline = useOnlineStatus();
    // 测试/复习会话开始时间(毫秒时间戳), 用于计算本次学习的实际时长
    const sessionStartRef = useRef(Date.now());
    const [words, setWords] = useState([]);
    const [queue, setQueue] = useState([]);        // 当前测试队列
    const [wrongQueue, setWrongQueue] = useState([]); // 错词重考队列
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [phase, setPhase] = useState('ready');     // ready, testing, feedback, copy, retest_info, complete
    const [userInput, setUserInput] = useState("");
    const [copyInput, setCopyInput] = useState("");
    const [isCorrect, setIsCorrect] = useState(false);
    const [isRetesting, setIsRetesting] = useState(false);

    const inputRef = useRef(null);
    const copyRef = useRef(null);
    const submittedRef = useRef(false);  // 防止完成时重复提交进度
    const currentWord = queue[currentIndex];

    // 离线时检查可学习范围 (规格 7: 仅未完成组 + 最后完成组后5组可离线学习)
    useEffect(() => {
        if (isOnline) return;
        getLearnableGroups().then(groups => {
            if (!groups.includes(groupId)) {
                alert('该组不可离线学习（仅未完成组与最后完成组后的5组可离线学习）');
                navigate('/');
            }
        });
    }, [isOnline, groupId, navigate]);

    // 获取单词数据 (在线走网络, 离线降级读 IndexedDB)
    useEffect(() => {
        api.getWords(groupId)
            .then(res => setWords(res.data))
            .catch(async () => {
                const local = await getWordsByGroup(groupId);
                setWords(local);
            });
    }, [groupId]);

    // 开始测试：随机打乱
    const startTest = useCallback(() => {
        const shuffled = [...words].sort(() => Math.random() - 0.5);
        setQueue(shuffled);
        setWrongQueue([]);
        setCurrentIndex(0);
        setTotalCount(shuffled.length);
        setPhase('testing');
        setUserInput("");
        setCopyInput("");
        setIsRetesting(false);
    }, [words]);

    // 自动聚焦输入框
    useEffect(() => {
        if (phase === 'testing' && inputRef.current) {
            inputRef.current.focus();
        }
        if (phase === 'copy' && copyRef.current) {
            copyRef.current.focus();
        }
    }, [phase, currentIndex]);

    // 朗读：通过后端 TTS 代理（有道美式发音）播放一遍
    const speak = useCallback((text) => {
        if (!text) return;
        const audio = new Audio(api.ttsUrl(text));
        audio.play().catch(err => console.error("TTS play failed:", err));
    }, []);

    // 提交答案
    const checkAnswer = useCallback(() => {
        if (!currentWord || !userInput.trim()) return;
        const correct = currentWord.word.trim().toLowerCase();
        const answer = userInput.trim().toLowerCase();
        const ok = answer === correct;

        setIsCorrect(ok);
        setPhase('feedback');
        speak(currentWord.word);

        if (!ok) {
            setWrongQueue(prev => {
                if (!prev.find(w => w.id === currentWord.id)) {
                    return [...prev, currentWord];
                }
                return prev;
            });
        }
    }, [currentWord, userInput, speak]);

    // 显示抄写区
    const showCopy = useCallback(() => {
        setCopyInput("");
        setPhase('copy');
    }, []);

    // 下一个词
    const nextWord = useCallback(() => {
        if (currentIndex + 1 < queue.length) {
            setCurrentIndex(prev => prev + 1);
            setUserInput("");
            setCopyInput("");
            setPhase('testing');
        } else {
            // 本轮结束
            if (wrongQueue.length > 0) {
                // 有错题，进入重考
                setPhase('retest_info');
            } else {
                // 全部通过
                setPhase('complete');
            }
        }
    }, [currentIndex, queue.length, wrongQueue.length]);

    // 开始重考
    const startRetest = useCallback(() => {
        const retestWords = [...wrongQueue].sort(() => Math.random() - 0.5);
        setQueue(retestWords);
        setWrongQueue([]);
        setCurrentIndex(0);
        setTotalCount(retestWords.length);
        setUserInput("");
        setCopyInput("");
        setPhase('testing');
        setIsRetesting(true);
    }, [wrongQueue]);

    // 完成测试，记录进度（op_type 依据活动类型：test / review1 / review2 / review3）
    const finishTest = useCallback(async () => {
        if (submittedRef.current) return;   // 防重入(按住Enter等场景)
        submittedRef.current = true;
        const now = localTimestamp();
        // 本次测试/复习实际时长(秒) = 完成时刻 - 会话开始时刻
        const durationSec = Math.max(0, Math.round((Date.now() - sessionStartRef.current) / 1000));
        // 本地优先: 先写本地 dirty (必定保存, 不丢记录)
        await putProgress(groupId, activityType, now, true, durationSec);
        // 在线则后台自动同步 (成功清 dirty, 失败保留)
        if (isOnline) triggerAutoSync();
        navigate('/');
    }, [groupId, navigate, activityType, isOnline]);

    // 键盘事件
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            if (phase === 'testing') {
                checkAnswer();
            } else if (phase === 'feedback' && isCorrect) {
                nextWord();
            } else if (phase === 'copy' && copyInput.toLowerCase() === currentWord.word.toLowerCase()) {
                nextWord();
            } else if (phase === 'feedback' && !isCorrect) {
                showCopy();
            }
        }
    }, [phase, isCorrect, checkAnswer, nextWord, showCopy, copyInput, currentWord]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // 阻止复制/剪切/粘贴/右键，防止单词被复制后粘贴作弊
    const blockClipboard = (e) => e.preventDefault();

    // 只保留 ASCII 可见字符并转小写，过滤中文/全角/emoji，同时压制首字母自动大写
    // 即使中文输入法开启或系统自动大写，输入框也只显示小写英文
    const sanitize = (s) => s.replace(/[^\x20-\x7E]/g, '').toLowerCase();

    // 输入框统一属性：强制英文输入、禁用中文输入法/自动大写/自动补全/粘贴
    const inputProps = {
        type: "text",
        lang: "en",
        inputMode: "latin",          // 唤起英文/拉丁字母软键盘
        autoComplete: "off",
        autoCorrect: "off",
        autoCapitalize: "off",       // 首字母不大写
        spellCheck: false,
        onPaste: blockClipboard,
        onContextMenu: blockClipboard,
        onCompositionStart: blockClipboard, // 阻止输入法联想
    };

    // 准备阶段
    if (words.length === 0) return <div className="loading">加载中...</div>;

    if (phase === 'ready') {
        return (
            <div
                className="test-container"
                onCopy={blockClipboard}
                onCut={blockClipboard}
                onPaste={blockClipboard}
                onContextMenu={blockClipboard}
            >
                <div className="test-card test-start-card">
                    <h1>单元测试 - Group {groupId}</h1>
                    <p className="test-info">共 {words.length} 个单词</p>
                    <p className="test-desc">根据中文含义拼写英文单词，拼写错误需要抄写纠正</p>
                    <button className="action-btn start-btn" onClick={startTest}>开始测试</button>
                    <button className="back-home" onClick={() => navigate('/')}>返回首页</button>
                </div>
            </div>
        );
    }

    if (phase === 'complete') {
        return (
            <div
                className="test-container"
                onCopy={blockClipboard}
                onCut={blockClipboard}
                onPaste={blockClipboard}
                onContextMenu={blockClipboard}
            >
                <div className="test-card test-complete-card">
                    <h1>🎉 测试完成！</h1>
                    <p>Group {groupId} 全部通过！</p>
                    <button className="action-btn" onClick={finishTest} disabled={submittedRef.current}>完成</button>
                </div>
            </div>
        );
    }

    if (phase === 'retest_info') {
        return (
            <div
                className="test-container"
                onCopy={blockClipboard}
                onCut={blockClipboard}
                onPaste={blockClipboard}
                onContextMenu={blockClipboard}
            >
                <div className="test-card test-retest-card">
                    <h1>📝 开始重考</h1>
                    <p>还有 {wrongQueue.length} 个错题需要重考</p>
                    <button className="action-btn" onClick={startRetest}>开始重考</button>
                </div>
            </div>
        );
    }

    if (!currentWord) return null;

    return (
        <div
            className="test-container"
            onCopy={blockClipboard}
            onCut={blockClipboard}
            onPaste={blockClipboard}
            onContextMenu={blockClipboard}
        >
            <div className="test-card">
                {/* 状态栏 */}
                <div className="test-header">
                    <span className="test-status">
                        {isRetesting ? '重考' : '测试'} - Group {groupId}
                    </span>
                    <span className="test-progress">
                        {currentIndex + 1} / {totalCount}
                    </span>
                </div>

                {/* 答题区域 */}
                <div className="test-body">
                    {phase === 'testing' && (
                        <div className="testing-area">
                            <div className="prompt-info">
                                <p className="prompt-chinese">{currentWord.chinese_meaning}</p>
                                <p className="prompt-pos">({currentWord.part_of_speech})</p>
                            </div>
                            <div className="input-row">
                                <input
                                    ref={inputRef}
                                    value={userInput}
                                    onChange={(e) => setUserInput(sanitize(e.target.value))}
                                    placeholder="请输入英文单词..."
                                    className="test-input"
                                    {...inputProps}
                                />
                                <button className="action-btn" onClick={checkAnswer}>提交</button>
                            </div>
                        </div>
                    )}

                    {/* 反馈区域 */}
                    {(phase === 'feedback' || phase === 'copy') && (
                        <div className="feedback-wrapper">
                            <div className="feedback-main">
                                <div className={`fb-status ${isCorrect ? 'correct' : 'wrong'}`}>
                                    {isCorrect ? '✅ 正确！' : `❌ 错误，你的答案: ${userInput}`}
                                </div>

                                <div className="fb-word">{currentWord.word}</div>
                                <div className="fb-chinese">中文: {currentWord.chinese_meaning}</div>
                                <div className="fb-pos">词性: {currentWord.part_of_speech}</div>

                                {currentWord.word_root && (
                                    <div className="fb-root">词根: {currentWord.word_root}</div>
                                )}
                                {currentWord.affix && (
                                    <div className="fb-affix">组成: {currentWord.affix}</div>
                                )}
                                {currentWord.example_sentence && (
                                    <div className="fb-sentence">例句: {currentWord.example_sentence}</div>
                                )}

                                {!isCorrect && phase === 'feedback' && (
                                    <button className="action-btn copy-trigger" onClick={showCopy}>
                                        抄写纠正 (Enter)
                                    </button>
                                )}

                                {isCorrect && (
                                    <button className="action-btn" onClick={nextWord}>
                                        下一词 (Enter)
                                    </button>
                                )}
                            </div>

                            {/* 图片 */}
                            {currentWord.picture_id && (
                                <div className="fb-image">
                                    <img src={api.pictureUrl(currentWord.picture_id)} alt="mindmap" />
                                </div>
                            )}
                        </div>
                    )}

                    {/* 抄写纠正区域 */}
                    {phase === 'copy' && (
                        <div className="copy-area">
                            <label className="copy-label">抄写: </label>
                            <input
                                ref={copyRef}
                                value={copyInput}
                                onChange={(e) => setCopyInput(sanitize(e.target.value))}
                                className={`copy-input ${copyInput.toLowerCase() === currentWord.word.toLowerCase() ? 'correct' : ''}`}
                                placeholder="请在此抄写正确单词..."
                                {...inputProps}
                            />
                            <span className={`copy-check ${copyInput.toLowerCase() === currentWord.word.toLowerCase() ? 'ok' : ''}`}>
                                {copyInput.toLowerCase() === currentWord.word.toLowerCase() ? '✅' : '❌'}
                            </span>
                            {copyInput.toLowerCase() === currentWord.word.toLowerCase() && (
                                <button className="action-btn" onClick={nextWord}>
                                    下一词 (Enter)
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <button className="back-home" onClick={() => navigate('/')}>返回首页</button>
        </div>
    );
}

export default TestMode;
